import type { FeatureApiResponse } from '@growthbook/growthbook';
import { GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import getItDeliveredImage from '@/assets/home/hero/get-it-delivered.jpg';
import pickItUpImage from '@/assets/home/hero/pick-it-up.jpg';
import rentACarImage from '@/assets/home/hero/rent-a-car.jpg';
import homepageThumbnail from '@/assets/home/homepage-social-media-thumbnail.jpg';
import RideLogo from '@/assets/icons/ride-logo.svg';
import { BecomeAHost } from '@/components/home/BecomeAHost';
import { Hero } from '@/components/home/Hero';
import { Recommendations } from '@/components/home/Recommendations';
import { SectionWrapper } from '@/components/home/SectionWrapper';
import { SeoLinks } from '@/components/home/SeoLinks';
import { SignatureDestinations } from '@/components/home/SignatureDestinations';
import HeadMeta from '@/components/static-pages/HeadMeta';
import { FavouriteListings } from '@/components/ui/FavouriteListings';
import { NationalParks } from '@/components/ui/NationalParks/NationalParks';
import { RecentlyViewedRentalsCarousel } from '@/components/ui/RecentlyViewed';
import { signatureDestination } from '@/constants/home/signatureDestinationsAndParks';
import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useGeoDataMyIp } from '@/hooks/useGeoDataMyIp';
import { useSectionIsInView } from '@/hooks/useSectionIsInView';
import { useShowRideLinks } from '@/hooks/useShowRideLinks';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { getHeaderSearchFilters } from '@/redux/selectors/globalHeader';
import { ESearchFilterCategory } from '@/services/analytics/search/types';
import { ERentalType } from '@/services/analytics/types';
import { useGrowthBookFeature } from '@/services/experiments/growthbook';
import { getGrowthBookPayload } from '@/services/experiments/growthbookPayload';
import { getSeoLinks, TSeoHomeSection } from '@/services/getHomePageData';
import { getRideBaseUrl } from '@/utility/getRideBaseUrl';
import { isProdDeploy } from '@/utility/isSSR';

interface IProps {
  growthbookPayload: FeatureApiResponse | null;
  isHomePage: boolean;
  isNewHomepage: boolean;
  locale: string;
  seoHomeData: TSeoHomeSection[];
}

const HomePage: NextPage<IProps> = ({ growthbookPayload, seoHomeData, locale }) => {
  const intl = useIntl();
  const showRideLinks = useShowRideLinks();
  const rideBadgeAllNew = useGrowthBookFeature('home-ride-tile-all-new-badge', {
    shouldRun: showRideLinks,
    payload: growthbookPayload,
  });
  const isProd = isProdDeploy();
  const dispatch = useDispatch();
  const { manual_address_input } = useSelector(getHeaderSearchFilters);

  const { isMobile } = useBreakpoint();

  const { data: myIpGeoData } = useGeoDataMyIp();

  const myIPAddress =
    myIpGeoData?.city && myIpGeoData?.state
      ? `${myIpGeoData.city}, ${myIpGeoData.state}`
      : undefined;

  useEffect(() => {
    if (myIPAddress && !manual_address_input) {
      dispatch(
        setHeaderSearchFilter({
          [ESearchFilters.ADDRESS]: myIPAddress,
          [ESearchFilters.MANUAL_ADDRESS_INPUT]: false,
        }),
      );
    }
  }, [myIPAddress, manual_address_input, dispatch]);

  const [nationalParksSectionRef, nationalParksInView] = useSectionIsInView();
  const [signatureDestinationsSectionRef, signatureDestinationsInView] = useSectionIsInView();
  const [becomeAHostSectionRef, becomeAHostInView] = useSectionIsInView();
  const [seoLinksSectionRef, seoLinksInView] = useSectionIsInView();

  useEffect(() => {
    if (nationalParksInView) {
      document.body.style.setProperty('--page-content-bg-color', '#D4DBD9');
      document.body.style.setProperty('--page-header-bg-color', '#D4DBD9');
      document.body.style.setProperty('--page-footer-bg-color', '#D4DBD9');
      return;
    }

    if (signatureDestinationsInView) {
      document.body.style.setProperty('--page-content-bg-color', '#C8D7BA');
      document.body.style.setProperty('--page-header-bg-color', '#C8D7BA');
      document.body.style.setProperty('--page-footer-bg-color', '#C8D7BA');
      return;
    }

    if (seoLinksInView || becomeAHostInView) {
      document.body.style.setProperty('--page-content-bg-color', 'var(--page-bg-white)');

      if (isMobile) {
        document.body.style.setProperty('--page-header-bg-color', 'var(--page-bg-beige)');
        document.body.style.setProperty('--page-footer-bg-color', 'var(--page-bg-beige)');
      } else {
        document.body.style.setProperty('--page-header-bg-color', 'var(--page-bg-white)');
        document.body.style.setProperty('--page-footer-bg-color', 'var(--page-bg-white)');
      }

      return;
    }

    document.body.style.removeProperty('--page-content-bg-color');
    document.body.style.removeProperty('--page-header-bg-color');
    document.body.style.removeProperty('--page-footer-bg-color');
  }, [
    nationalParksInView,
    signatureDestinationsInView,
    becomeAHostInView,
    seoLinksInView,
    isMobile,
  ]);

  // Clear the inline body vars on unmount so the homepage's scroll colors
  // don't leak into the next client-side-navigated page.
  useEffect(
    () => () => {
      ['--page-content-bg-color', '--page-header-bg-color', '--page-footer-bg-color'].forEach(v =>
        document.body.style.removeProperty(v),
      );
    },
    [],
  );

  // Left off until the test resolves, so visitors never see "New" swap to "All new".
  let rideBadge: string | undefined;
  if (rideBadgeAllNew === true) {
    rideBadge = intl.formatMessage({
      id: 'T9S3sU',
      defaultMessage: 'All new',
      description:
        'Home > Hero > Rent a car card > badge pill over the image marking cars as a new offering (GrowthBook test variant)',
    });
  } else if (rideBadgeAllNew === false) {
    rideBadge = intl.formatMessage({
      id: 'Y3WoQ8',
      defaultMessage: 'New',
      description:
        'Home > Hero > Rent a car card > badge pill over the image marking cars as a new offering',
    });
  }

  // Hardcoded data for now, to be replace with BE data when available
  const signatureDestinations = signatureDestination(intl, isProd);

  return (
    <>
      <HeadMeta
        thumbnailURL={homepageThumbnail.src}
        description={intl.formatMessage({
          defaultMessage:
            'Rent an RV, motorhome, trailer, or campervan from Outdoorsy for your next adventure. Outdoorsy is the #1 most trusted RV rental marketplace in the world',
          id: '/jiUAQ',
        })}
      />

      <Head>
        <style>{`
          body {
            --page-content-bg-color: var(--page-bg-beige);
            --page-header-bg-color: var(--page-bg-beige);
            --page-footer-bg-color: var(--page-bg-beige);
          }
        `}</style>
      </Head>

      <SectionWrapper>
        <Hero
          headline={[
            intl.formatMessage({
              id: '7XZCbR',
              defaultMessage: 'Start your adventure',
              description: 'Home > Headline > 1st line',
            }),
            [
              intl.formatMessage({
                id: 'rACY5D',
                defaultMessage: 'with an RV rental',
                description: 'Home > Headline > 2nd line',
              }),
              intl.formatMessage({
                id: 'gIFd7w',
                defaultMessage: 'with a camper van',
                description: 'Home > Headline > 2nd line',
              }),
              intl.formatMessage({
                id: '5KpQy3',
                defaultMessage: 'with a travel trailer',
                description: 'Home > Headline > 2nd line',
              }),
            ],
          ]}
          cards={[
            {
              background: getItDeliveredImage.src,
              title: intl.formatMessage(
                {
                  id: 'Jhm3co',
                  defaultMessage: 'Less set up.{br}More quality time.',
                  description: 'Home > Hero > Predefined searches',
                },
                { br: <br /> },
              ),
              description: intl.formatMessage({
                id: 'DbXJwy',
                defaultMessage: `Love camping, but don't love driving? Get an RV delivered and set up at a location of your choice.`,
                description: 'Home > Hero > Predefined searches',
              }),
              cta: intl.formatMessage({
                id: '+1fAH8',
                defaultMessage: 'Get it delivered',
                description: 'Home > Hero > Predefined searches',
              }),
              url: '/rv-search?=&delivery=true&deliveryStationary=stationary&openStationaryDeliveryModal=true',
              searchFilterCategory: ESearchFilterCategory.GET_IT_DELIVERED,
            },
            {
              background: pickItUpImage.src,
              title: intl.formatMessage(
                {
                  id: 'NcIyH9',
                  defaultMessage: 'Pick it up &{br}hit the road',
                  description: 'Home > Hero > Predefined searches',
                },
                { br: <br /> },
              ),
              description: intl.formatMessage({
                id: 'HmPMvE',
                defaultMessage: `Get ready for the ultimate road trip. Pick up an RV near you, or at a destination of your choice.`,
                description: 'Home > Hero > Predefined searches',
              }),
              cta: intl.formatMessage({
                id: 'HEcT5J',
                defaultMessage: 'Pick it up',
                description: 'Home > Hero > Predefined searches',
              }),
              url: '/rv-search',
              searchFilterCategory: ESearchFilterCategory.PICK_IT_UP,
            },
            // Ride has no cars outside the US, so the card would open an empty marketplace.
            ...(showRideLinks
              ? [
                  {
                    background: rentACarImage.src,
                    badge: rideBadge,
                    title: intl.formatMessage(
                      {
                        id: 'sokXwp',
                        defaultMessage: "Just need a car?{br}We've got those too.",
                        description: 'Home > Hero > Predefined searches > Rent a car card title',
                      },
                      { br: <br /> },
                    ),
                    description: intl.formatMessage({
                      id: 'Tf6XRi',
                      defaultMessage: `Rent one from local hosts on Ride, part of the Outdoorsy family.`,
                      description:
                        'Home > Hero > Predefined searches > Rent a car card description',
                    }),
                    cta: intl.formatMessage({
                      id: 'DPYqQk',
                      defaultMessage: 'Rent a car',
                      description: 'Home > Hero > Predefined searches > Rent a car card CTA',
                    }),
                    ctaAside: (
                      <span className="flex flex-col items-start gap-0.5">
                        <RideLogo role="img" aria-label="Ride" className="w-auto h-[18px]" />
                        <span className="text-xs whitespace-nowrap">
                          {intl.formatMessage({
                            id: 'DTJe8X',
                            defaultMessage: 'An Outdoorsy Company',
                            description:
                              'Home > Hero > Predefined searches > Rent a car card > Ride brand attribution beside the CTA button',
                          })}
                        </span>
                      </span>
                    ),
                    imageAlt: intl.formatMessage({
                      id: '33TB+z',
                      defaultMessage: 'Rent a car on Ride – An Outdoorsy Company',
                      description:
                        'Home > Hero > Predefined searches > Rent a car card image alt text',
                    }),
                    url: `${getRideBaseUrl()}/?utm_source=outdoorsy&utm_medium=homepage&utm_campaign=hero_card`,
                    searchFilterCategory: ESearchFilterCategory.RENT_A_CAR,
                    rentalType: ERentalType.AUTO,
                    dimBackground: true,
                  },
                ]
              : []),
          ]}
          locale={locale}
          myIPAddress={myIPAddress}
        />
      </SectionWrapper>

      <SectionWrapper>
        <RecentlyViewedRentalsCarousel className="px-6 lg:px-20" />
      </SectionWrapper>

      <SectionWrapper>
        <FavouriteListings className="px-6 lg:px-20" />
      </SectionWrapper>

      <SectionWrapper>
        <Recommendations myIPAddress={myIPAddress} />
      </SectionWrapper>

      <SectionWrapper ref={nationalParksSectionRef}>
        <NationalParks />
      </SectionWrapper>

      <SectionWrapper ref={signatureDestinationsSectionRef}>
        <SignatureDestinations signatureDestinations={signatureDestinations} />
      </SectionWrapper>

      <SectionWrapper ref={becomeAHostSectionRef} className="hidden">
        <BecomeAHost />
      </SectionWrapper>

      {!!seoHomeData.length && (
        <SectionWrapper ref={seoLinksSectionRef}>
          <SeoLinks seoData={seoHomeData} />
        </SectionWrapper>
      )}
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const [seoHomeData, growthbookPayload] = await Promise.all([
    getSeoLinks(locale),
    getGrowthBookPayload(),
  ]);

  return {
    revalidate: 180,
    props: {
      growthbookPayload,
      isHomePage: true,
      locale,
      seoHomeData,
    },
  };
};

export default HomePage;
